import styled from 'styled-components';

export const NewsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 290px) minmax(0, 1fr);
  gap: var(--space-xxl);
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--space-xl) var(--space-l) var(--space-xxxl);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: var(--space-xl);
  }
`;

export const Sidebar = styled.aside`
  border-right: 1px solid var(--grey);
  padding-right: var(--space-l);

  @media (max-width: 900px) {
    /* hide desktop sidebar on small screens; drawer will be used instead */
    display: none;
  }
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

export const ListItem = styled.li`
  position: relative;

  padding: var(--space-s) var(--space-m);

  border-radius: var(--borderRadiusSmall);

  cursor: pointer;

  background: ${({ $active }) => ($active ? 'var(--yellow1)' : 'transparent')};

  color: ${({ $active }) => ($active ? 'var(--grey7)' : 'var(--grey5)')};

  font-family: var(--mainFont);
  font-size: 0.95rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  line-height: 1.4;

  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;

    width: 3px;
    height: ${({ $active }) => ($active ? '55%' : '0')};

    background: var(--yellow5);
    border-radius: 10px;

    transform: translateY(-50%);

    transition: height 0.2s ease;
  }

  &:hover {
    background: var(--grey15);
    color: var(--grey7);
    transform: translateX(2px);
  }
`;

export const Content = styled.section`
  min-width: 0;
`;

export const NewsletterArticle = styled.article`
  max-width: 850px;

  background: var(--white);

  border: 1px solid var(--grey);
  border-radius: var(--borderRadiusLarge);

  box-shadow: var(--shadow-md);

  overflow: hidden;
`;

export const NewsletterHeader = styled.header`
  position: relative;

  padding: clamp(2rem, 5vw, 4.5rem) clamp(1.5rem, 5vw, 4rem)
    clamp(2rem, 4vw, 3rem);

  background: linear-gradient(
    135deg,
    rgba(255, 240, 194, 0.8),
    rgba(255, 255, 255, 0.96) 62%
  );

  border-bottom: 1px solid var(--grey);

  &::after {
    content: '';

    position: absolute;
    top: 0;
    right: 0;

    width: 110px;
    height: 8px;

    background: var(--yellow5);
  }
`;

export const NewsletterMonth = styled.p`
  margin: 0 0 var(--space-s);

  color: var(--orange5);

  font-family: var(--mainFont);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const NewsletterTitle = styled.h1`
  max-width: 700px;

  margin: 0;

  color: var(--grey7);

  font-family: var(--headerFont);
  font-size: clamp(2.2rem, 5vw, 4.5rem);
  font-weight: 600;
  line-height: 0.98;
  letter-spacing: -0.02em;
`;

export const NewsletterExcerpt = styled.p`
  max-width: 650px;

  margin: var(--space-l) 0 0;

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: clamp(1rem, 2vw, 1.15rem);
  line-height: 1.7;
`;

export const NewsletterSections = styled.div`
  padding: var(--space-xl) clamp(1.5rem, 5vw, 4rem) var(--space-xxl);
`;

export const NewsletterSection = styled.section`
  padding: var(--space-xl) 0;

  border-bottom: 1px solid var(--grey15);

  &:first-child {
    padding-top: var(--space-m);
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const SectionTitle = styled.h2`
  position: relative;

  margin: 0 0 var(--space-l);

  color: var(--grey7);

  font-family: var(--headerFont);
  font-size: clamp(1.65rem, 3vw, 2.25rem);
  font-weight: 600;
  line-height: 1.15;

  &::after {
    content: '';

    display: block;

    width: 44px;
    height: 4px;

    margin-top: var(--space-s);

    background: var(--yellow5);
    border-radius: 20px;
  }
`;

export const NewsletterBody = styled.div`
  color: var(--grey5);

  font-family: var(--mainFont);
  font-size: 1rem;
  line-height: 1.8;

  p {
    margin: 0 0 var(--space-m);
  }

  p:last-child {
    margin-bottom: 0;
  }

  strong {
    color: var(--grey7);
    font-weight: 700;
  }

  ul,
  ol {
    margin: var(--space-m) 0;
    padding-left: var(--space-l);
  }

  li {
    margin-bottom: var(--space-xs);
  }

  a {
    color: var(--blue4);
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  blockquote {
    margin: var(--space-l) 0;
    padding: var(--space-m) var(--space-l);

    background: var(--yellow1);

    border-left: 4px solid var(--yellow5);
    border-radius: 0 var(--borderRadiusSmall) var(--borderRadiusSmall) 0;

    color: var(--grey6);
  }

  h3 {
    margin: var(--space-xl) 0 var(--space-s);

    color: var(--grey7);

    font-family: var(--headerFont);
    font-size: 1.4rem;
  }
`;

export const Empty = styled.div`
  padding: var(--space-xl);

  color: var(--grey4);

  background: var(--offWhite);

  border: 1px dashed var(--grey2);
  border-radius: var(--borderRadius);

  font-family: var(--mainFont);
  text-align: center;
`;

// Mobile drawer styles
export const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: flex-start;
  z-index: 60;
`;

export const DrawerContent = styled.div`
  width: min(360px, 90vw);
  height: 100%;
  background: var(--white);
  padding: var(--space-l);
  box-shadow: var(--shadow-lg);
  overflow-y: auto;
  transform: translateX(-100%);
  animation: slideIn 220ms cubic-bezier(0.2, 0.9, 0.2, 1) forwards;

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0%);
    }
  }
`;

export const MobileToggle = styled.button`
  display: none;
  position: fixed;
  left: 16px;
  bottom: 24px;
  z-index: 70;
  background: var(--yellow5);
  color: var(--grey7);
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 900px) {
    display: block;
  }
`;

export const DrawerSidebar = styled.aside`
  display: block;
  width: 100%;
  padding-right: 0;
  padding-bottom: var(--space-l);

  ${List} {
    margin-top: var(--space-s);
  }
`;
